import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { AlertTriangle, CheckCircle, FileUp, Rocket, RefreshCw, ShieldCheck } from 'lucide-react';
import PageHeader from '../Shared/PageHeader';
import ConfirmDialog from '../common/ConfirmDialog';
import permissionsService from '../../services/permissionsService';
import {
  PROMOTE_STUDY_BUILD_PERMISSION,
  deployPackage,
  getDeploymentHistory,
  preflightPackage,
  readErrorMessage,
  verifyDeployment,
} from '../../services/studyPromotionApi';
import PreflightReportView from './PreflightReportView';
import VerificationReportView from './VerificationReportView';

const STATUS_BADGES = {
  deployed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  noop: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

// FECOA-1920. "Not verified" is deliberately neutral rather than a warning: a
// deploy nobody has checked yet is not the same as one that failed its check.
const VERIFICATION_BADGES = {
  match: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  mismatch: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  unverifiable: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

const VERIFICATION_LABELS = {
  match: 'Verified',
  mismatch: 'Drifted',
  unverifiable: 'Unverifiable',
};

const formatTimestamp = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const StudyPromotionMain = () => {
  const navigate = useNavigate();

  const [hasAccess, setHasAccess] = useState(null);
  const [target, setTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [packagePayload, setPackagePayload] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [openVerification, setOpenVerification] = useState(null);

  useEffect(() => {
    permissionsService
      .hasPermission(PROMOTE_STUDY_BUILD_PERMISSION)
      .then(setHasAccess)
      .catch(() => setHasAccess(false));
  }, []);

  useEffect(() => {
    if (hasAccess === false) {
      const timer = setTimeout(() => {
        navigate('/studies', {
          state: {
            accessDenied: true,
            message: 'You do not have permission to promote study builds.',
          },
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasAccess, navigate]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await getDeploymentHistory();
      setHistory(data.deployments || []);
      setTarget(data.target || null);
    } catch (err) {
      console.error('Failed to load deployment history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAccess) loadHistory();
  }, [hasAccess, loadHistory]);

  // FECOA-1920. Re-verify on demand: a build that was green at deploy time can
  // drift later, so this is a check the operator can repeat, not a one-off.
  const runVerification = useCallback(async (deploymentId) => {
    setVerifyingId(deploymentId);
    try {
      const data = await verifyDeployment(deploymentId);
      const verification = data.verification;
      setOpenVerification({ deploymentId, verification });
      setHistory((rows) => rows.map((row) => (
        row.id === deploymentId ? { ...row, latest_verification: verification } : row
      )));
      if (verification.ok) {
        toast.success('Target matches the candidate on every section');
      } else {
        toast.error(verification.detail || 'Verification did not pass');
      }
    } catch (err) {
      toast.error(await readErrorMessage(err, 'Verification failed'));
    } finally {
      setVerifyingId(null);
    }
  }, []);

  const runPreflight = async (file) => {
    setSelectedFile(file);
    setPackagePayload(null);
    setPreflight(null);
    setDeployResult(null);
    setPreflightLoading(true);
    try {
      const data = await preflightPackage(file);
      setPreflight(data);
      setTarget(data.target || null);
      // Parse the package locally too, for the deep planned-changes review.
      // The server report stays the authority; this is display only.
      try {
        setPackagePayload(JSON.parse(await file.text()).payload || null);
      } catch {
        setPackagePayload(null);
      }
    } catch (err) {
      setSelectedFile(null);
      toast.error(await readErrorMessage(err, 'That file is not a valid candidate package.'));
    } finally {
      setPreflightLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length) runPreflight(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/json': ['.json'] },
    multiple: false,
  });

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      const result = await deployPackage(selectedFile);
      setDeployResult(result);
      setConfirmOpen(false);
      if (result.status === 'noop') {
        toast.error(result.message || 'This candidate is already deployed; no changes were made.');
      } else {
        toast.success(
          result.status === 'replaced'
            ? `Candidate deployed — the previous build of ${result.study.title} was replaced.`
            : `Candidate deployed — ${result.study.title} was created in this environment.`
        );
      }
      loadHistory();
    } catch (err) {
      setConfirmOpen(false);
      const data = err?.response?.data;
      if (data?.report) {
        setPreflight((prev) => (prev ? { ...prev, report: data.report } : prev));
      }
      toast.error(data?.error || 'Deploy failed. Nothing was written.');
      loadHistory();
    } finally {
      setDeploying(false);
    }
  };

  if (hasAccess === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-8 text-center max-w-md">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            You do not have permission to promote study builds.
          </p>
          <button
            onClick={() => navigate('/studies')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Studies
          </button>
        </div>
      </div>
    );
  }

  const manifest = preflight?.manifest;
  const report = preflight?.report;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <PageHeader
          icon={Rocket}
          title="Study Promotion"
          subtitle={
            target
              ? `Deploy a verified candidate build into ${target.environment} (v${target.application_version})`
              : 'Deploy a verified candidate build into this environment'
          }
        />

        {/* Upload */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6 mb-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-primary-500'
            }`}
          >
            <input {...getInputProps()} />
            <FileUp className="w-10 h-10 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              {selectedFile ? selectedFile.name : 'Drop a candidate package here, or click to browse'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              .fxbuild.json — exported from the source environment's study page
            </p>
          </div>
          {preflightLoading && (
            <div className="flex items-center justify-center gap-2 mt-4 text-gray-600 dark:text-gray-300">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary-600"></div>
              Running preflight checks...
            </div>
          )}
        </div>

        {/* Preflight report */}
        {report && !deployResult && (
          <>
            <PreflightReportView manifest={manifest} target={target} report={report} payload={packagePayload} />
            <div className="flex items-center justify-end gap-3 mt-6 mb-6">
              {!report.ok && (
                <p className="text-sm text-red-600 dark:text-red-400 mr-auto">
                  Deploy is blocked until every preflight check passes.
                </p>
              )}
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={!report.ok || deploying}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Deploy Candidate
              </button>
            </div>
          </>
        )}

        {/* Deploy result */}
        {deployResult && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {deployResult.status === 'replaced'
                    ? 'Candidate deployed (previous build replaced)'
                    : deployResult.status === 'noop'
                      ? 'Already deployed — no changes made'
                      : 'Candidate deployed'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {deployResult.study.title} ({deployResult.study.code}) in{' '}
                  {deployResult.target?.environment}. Audit reference: deployment record #
                  {deployResult.ledger_id}.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-mono break-all">
                  Candidate {deployResult.manifest?.candidate_id}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Post-deploy verification report (FECOA-1920) */}
        {openVerification && (
          <VerificationReportView
            verification={openVerification.verification}
            onClose={() => setOpenVerification(null)}
          />
        )}

        {/* Deployment history */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Deployment history
            </h3>
            <button
              onClick={loadHistory}
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${historyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No candidate builds have been deployed into this environment yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-6 font-medium">When</th>
                    <th className="py-2 pr-6 font-medium">Study</th>
                    <th className="py-2 pr-6 font-medium">Candidate</th>
                    <th className="py-2 pr-6 font-medium">From</th>
                    <th className="py-2 pr-6 font-medium">Status</th>
                    <th className="py-2 pr-6 font-medium">Deployed by</th>
                    <th className="py-2 font-medium">Verification</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {history.map((row) => (
                    <tr key={row.id}>
                      <td className="py-2 pr-6 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatTimestamp(row.deployed_at)}
                      </td>
                      <td className="py-2 pr-6 text-gray-900 dark:text-gray-100">
                        {row.study_title || row.study_code}
                        <span className="text-gray-500 dark:text-gray-400"> ({row.study_code})</span>
                      </td>
                      <td className="py-2 pr-6 text-gray-700 dark:text-gray-300">
                        {row.candidate_label}
                        <span className="block font-mono text-xs text-gray-500 dark:text-gray-400">
                          {row.candidate_id.slice(0, 8)}…
                        </span>
                      </td>
                      <td className="py-2 pr-6 text-gray-700 dark:text-gray-300">
                        {row.source_environment}
                      </td>
                      <td className="py-2 pr-6">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGES[row.status] || STATUS_BADGES.noop}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="py-2 pr-6 text-gray-700 dark:text-gray-300">
                        {row.deployed_by_email}
                      </td>
                      <td className="py-2">
                        {row.status !== 'deployed' ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            Not applicable
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            {row.latest_verification ? (
                              <button
                                type="button"
                                onClick={() => setOpenVerification({
                                  deploymentId: row.id,
                                  verification: row.latest_verification,
                                })}
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${VERIFICATION_BADGES[row.latest_verification.result] || VERIFICATION_BADGES.unverifiable}`}
                                title={row.latest_verification.detail}
                              >
                                {VERIFICATION_LABELS[row.latest_verification.result] || row.latest_verification.result}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Not verified
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => runVerification(row.id)}
                              disabled={verifyingId === row.id}
                              className="inline-flex items-center text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                            >
                              <ShieldCheck className={`w-3.5 h-3.5 mr-1 ${verifyingId === row.id ? 'animate-pulse' : ''}`} />
                              {verifyingId === row.id ? 'Verifying…' : 'Verify'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ConfirmDialog
          isOpen={confirmOpen}
          onClose={() => !deploying && setConfirmOpen(false)}
          onConfirm={handleDeploy}
          title="Deploy Candidate Build?"
          message={`This will deploy into the ${target?.environment || 'current'} environment:\n\nStudy: ${manifest?.study?.title} (${manifest?.study?.study_code})\nCandidate: ${manifest?.candidate_label}\nExported from: ${manifest?.source_environment}\n\nThe deployment is recorded in the audit trail under your account.`}
          confirmText="Deploy"
          type="warning"
          isLoading={deploying}
        />
      </div>
    </div>
  );
};

export default StudyPromotionMain;
